import clsx from 'clsx'
import type { PropsWithChildren } from 'react'
import React from 'react'
import type { AllColors } from '../utilities/colors'

export interface AvatarProps {
  'aria-label'?: string
  color?: AvatarColor
  name?: string
  variant?: AvatarVariant
}

export type AvatarColor = AllColors | 'green'
type AvatarColors = { [key in AllColors | 'green']: string }

type AvatarVariant = 'circle' | 'rounded' | 'square'
type AvatarVariants = {
  [key in AvatarVariant]: string
}

export const colors: AvatarColors = {
  default: 'bg-black-5 text-black-90',
  primary: 'bg-primary-10 text-primary-darker',
  secondary: 'bg-secondary-10 text-secondary-darker',
  blue: 'bg-blue-100 text-blue-700',
  error: 'bg-error-lighter text-error-dark',
  green: 'bg-teal-100 text-teal-800',
  indigo: 'bg-indigo-100 text-indigo-700',
  pink: 'bg-pink-100 text-pink-700',
  purple: 'bg-purple-100 text-purple-700',
  red: 'bg-red-100 text-red-700',
  teal: 'bg-teal-100 text-teal-800',
  warning: 'bg-warning-lighter text-warning-darker',
  yellow: 'bg-yellow-100 text-yellow-800',
}

const variants: AvatarVariants = {
  circle: 'rounded-full',
  square: '',
  rounded: 'rounded-md',
}

function Avatar({
  'aria-label': label,
  children,
  color = 'default',
  name,
  variant = 'circle',
}: PropsWithChildren<AvatarProps>): JSX.Element {
  return (
    <div
      aria-label={label || name}
      className={clsx(
        'justify-center items-center text-xl inline-flex w-10 h-10 p-2',
        colors[color],
        variants[variant],
      )}
      role="img"
    >
      {name !== undefined
        ? name.match(/[A-Z]/g)?.slice(0, 2)?.join('') ||
          name
            .split(' ')
            .slice(0, 2)
            .map((word) => word.substring(0, 1).toUpperCase())
            .join('')
        : typeof children === 'string'
        ? children.substring(0, 2).toUpperCase()
        : children}
    </div>
  )
}

export default Avatar
